import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { useSession } from '../Utils/SessionContext';
import usePopupClose from '../Utils/usePopupClose';
import JobSearchSelect from '../EvaluationComponents/Shards/CreateEvaluation/JobSearchSelect';

const AddTalentPopup = ({ isOpen, onClose, onSubmit }) => {
  const { user } = useSession();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    additionalLink: '',
    generateVsResumeEvaluation: true,
    generateTalentProfileAssessment: true
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = usePopupClose(isOpen, onClose);


  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        additionalLink: '',
        generateVsResumeEvaluation: true,
        generateTalentProfileAssessment: true
      });
      setResumeFile(null);
      setSelectedJob(null);
      setErrors({});
      setSubmitError(null);
      setIsSubmitting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setErrors({
          ...errors,
          resume: 'Only PDF files are allowed'
        });
        setResumeFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setResumeFile(file);
        if (errors.resume) {
          const newErrors = { ...errors };
          delete newErrors.resume;
          setErrors(newErrors);
        }
      }
    } else {
      setResumeFile(null);
    }
  };

  const handleJobSelect = (job) => {
    setSelectedJob(job);
    console.log('Selected job:', job);
  };

  const handleJobClear = () => {
    setSelectedJob(null);
    console.log('Job selection cleared');
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate email format if provided
    if (formData.email.trim() && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    // Validate resume file (required)
    if (!resumeFile) {
      newErrors.resume = 'Resume file is required';
    }
    
    // Additional link is optional, but if provided, validate it's a URL
    if (formData.additionalLink && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i.test(formData.additionalLink)) {
      newErrors.additionalLink = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {        
        // Create FormData object for file upload
        const formDataToSubmit = new FormData();
        formDataToSubmit.append('resume', resumeFile);
        formDataToSubmit.append('firstName', formData.firstName);
        formDataToSubmit.append('lastName', formData.lastName);
        formDataToSubmit.append('email', formData.email);
        
        // Ensure the additional link starts with https://
        let link = formData.additionalLink || '';
        if (link && !link.startsWith('http://') && !link.startsWith('https://')) {
          link = 'https://' + link;
        }
        formDataToSubmit.append('link', link);
        
        formDataToSubmit.append('generateVsResumeEvaluation', formData.generateVsResumeEvaluation);
        
        formDataToSubmit.append('job_id', selectedJob?.job_id || '');
        
        // Create and add action_data JSON object
        const action_data = {
          action: formData.generateTalentProfileAssessment ? "generate-talent-profile-assessment" : ""
        };
        formDataToSubmit.append('action_data', JSON.stringify(action_data));

        // Use NEW_TALENT with authentication headers
        console.log('Using token authentication');
        const response = await fetch(API_ENDPOINTS.NEW_TALENT, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.access_token}`
          },
          body: formDataToSubmit,
        });
        
        if (!response.ok) {
          const error = new Error(`Error: ${response.status} ${response.statusText}`);
          error.status = response.status; // Add status to the error object for use in catch block
          throw error;
        }
        
        const responseData = await response.json();
        console.log('Submission response:', responseData);
        
        // Notify the parent component that submission was successful
        if (onSubmit) {
          onSubmit(responseData);
        }
        
        onClose(); // Close popup on success
      } catch (error) {
        console.error('Error adding talent:', error);
        console.log('Error status code:', error.status);
        
        // If it's a 401 error, it's an expired session - notify the parent component
        if (error.status === 401) {
          console.log('Session expired or unauthorized.');
          setSubmitError('Your session has expired. Please log in again.');
          // We can't directly navigate from here since this is a popup component
          // Instead, we'll notify the parent component about the authentication error
          if (onSubmit) {
            onSubmit({ authError: true });
          }
          return;
        }
        
        setSubmitError(error.message || 'Failed to add talent. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Add New Talent</h2>
          <p className="text-gray-600 mt-1">Processing a new talent takes ~20 minutes (incl. all generation option).</p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Resume Upload */}
          <div>
            <label htmlFor="resume" className="block text-base font-medium text-gray-700 mb-2">
              <span className="text-red-500 text-xl">*</span> Resume (PDF)
            </label>
            <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
              errors.resume ? 'border-red-300' : 'border-gray-300'
            }`}>
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="resume-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="resume-upload"
                      name="resume-upload"
                      type="file"
                      className="sr-only"
                      accept=".pdf"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PDF up to 10MB</p>
                {resumeFile && (
                  <p className="text-sm text-green-600">
                    Selected: {resumeFile.name}
                  </p>
                )}
              </div>
            </div>
            {errors.resume && (
              <p className="mt-1 text-sm text-red-600">{errors.resume}</p>
            )}
          </div>
          
          {/* First Name, Last Name, and Email in 3 columns */}
          <div className="grid grid-cols-3 gap-4">
            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-base font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 text-base ${
                  errors.firstName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Jim"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>
            
            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-base font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 text-base ${
                  errors.lastName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Morrison"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>
            
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-base font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 text-base ${
                  errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="jm@doors.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
          </div>
          
          {/* Additional Link */}
          <div>
            <label htmlFor="additionalLink" className="block text-base font-medium text-gray-700 mb-2">
              Additional Link (e.g. LinkedIn, Portfolio, GitHub)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                id="additionalLink"
                name="additionalLink"
                value={formData.additionalLink}
                onChange={handleChange}
                className={`w-full pl-12 pr-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 text-base bg-white ${
                  errors.additionalLink ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="https://example.com"
              />
            </div>
            {errors.additionalLink && (
              <p className="mt-1 text-sm text-red-600">{errors.additionalLink}</p>
            )}
          </div>
          
          {/* Job Selection */}
          <div>
            <JobSearchSelect
              label="Assign to Job as Candidate"
              placeholder="Type to search for a job ..."
              selectedJob={selectedJob}
              onJobSelect={handleJobSelect}
              onClear={handleJobClear}
              clearable={true}
            />
          </div>
          
          {/* Generate Options */}
          <div className="space-y-4">
            {/* Generate VsResume Evaluation */}
            <div className="flex items-center">
              <input
                id="generateVsResumeEvaluation"
                name="generateVsResumeEvaluation"
                type="checkbox"
                checked={formData.generateVsResumeEvaluation}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="generateVsResumeEvaluation" className="ml-2 block text-sm font-medium text-gray-700">
                Generate First Evaluation (VSResume Criteria)
              </label>
            </div>
            
            {/* Generate Talent Profile Assessment */}
            <div className="flex items-center">
              <input
                id="generateTalentProfileAssessment"
                name="generateTalentProfileAssessment"
                type="checkbox"
                checked={formData.generateTalentProfileAssessment}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="generateTalentProfileAssessment" className="ml-2 block text-sm font-medium text-gray-700">
                Generate Profile Assessment
              </label>
            </div>
          </div>
          
          {/* Error Message */}
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Add Talent'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTalentPopup;
